import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, DocumentFilter, Add, Refresh2 } from "iconsax-react";
import { Input, Button, Select, DatePicker } from "@/shared/ui";
import { useLocale } from "@/shared/hooks";
import type { Locale } from "@/shared/utils";
import { CONTENT_TYPE_LABELS } from "../../../consts";

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  onUpload: () => void;
  disabled?: boolean;
  typeFilter: string | null;
  onTypeChange: (value: string | null) => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
}

function getTypeOptions(): { label: string; value: string }[] {
  const seen = new Set<string>();
  const options: { label: string; value: string }[] = [];
  for (const [mime, label] of Object.entries(CONTENT_TYPE_LABELS)) {
    if (!seen.has(label)) {
      seen.add(label);
      options.push({ label, value: mime });
    }
  }
  return options;
}

export default function AttachedDocumentsFilter({
  search,
  onSearchChange,
  onUpload,
  disabled,
  typeFilter,
  onTypeChange,
  dateRange,
  onDateRangeChange,
  hasActiveFilters,
  onResetFilters,
}: Props) {
  const { t } = useTranslation("AttachedDocumentsPage");
  const locale = useLocale() as Locale;
  const typeOptions = getTypeOptions();

  function handleDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onDateRangeChange(v);
  }

  return (
    <div className="flex items-center justify-between gap-2 mb-7 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="min-w-48">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.searchPlaceholder")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        <DatePicker
          value={dateRange}
          mode="range"
          onChange={handleDateRangeChange}
          locale={locale}
          placeholder={t("filters.period")}
          isSmall
          width="min-w-40"
          icon={<Calendar2 size={16} color="currentColor" />}
          align="left"
        />

        <div className="min-w-40">
          <Select
            options={typeOptions}
            placeholder={t("filters.fileType")}
            value={typeFilter}
            onChange={onTypeChange}
            isSmall
            icon={<DocumentFilter size={16} color="currentColor" />}
            width="w-40"
          />
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            className="px-3 h-8 flex justify-center items-center gap-2 content-base-low cursor-pointer"
            onClick={onResetFilters}>
            <Refresh2 size={16} color="currentColor" />
            <span className="text-label-sm">{t("filters.reset")}</span>
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button variant="primary" size="md" onClick={onUpload} disabled={disabled}>
          <Add size={16} color="currentColor" />
          {t("header.uploadDocument")}
        </Button>
      </div>
    </div>
  );
}
